import { Text, Modal, InlineStack, BlockStack, Grid, Form, Divider, FormLayout, TextField, Button, Select } from '@shopify/polaris';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import helpers from 'helpers/index';
import { useGetOrder, useMutateOrderCancel, useMutateUpdateOrder, ENUM_ORDER_FEE, ORDER_FEE_LABEL } from 'queries/orders.query';
import { ConfettiIcon, DuplicateIcon, PlusIcon } from '@shopify/polaris-icons';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import { TypedCustomer } from 'interface/customer.model';
import __ from 'languages/index';
import dateandtime from 'date-and-time';
import { useMutateSearchUser } from 'queries/users.query';
import { ENUM_TICKET_TYPE } from 'queries/ticket.query';

function takeNumberOnly(input: string): string {
  return input.replace(/\D/g, '');
}

export default function EditOrderModal({ show, order_id, onClose }: { show: boolean; order_id: string; onClose: (reload: boolean) => void }) {
  const { addNotification } = useNotification();
  const { mutateAsync: orderCancel, isPending: cancelling, isSuccess } = useMutateOrderCancel();
  const { mutateAsync: updateOrder, isPending: updating, isSuccess: updateSuccess } = useMutateUpdateOrder();
  const { data: orderData, isPending: loadingGetTicketViaOrderCode } = useGetOrder(order_id);

  const [currentCustomerData, setCurrentCustomerData] = useState<TypedCustomer>(null);

  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */

  useEffect(() => {
    if (orderData) {
      setCurrentCustomerData(orderData?.customer_data);
    }
  }, [orderData]);

  const invoiceFields = {
    invoice_tax_type: useField<string>({ value: orderData?.order_invoice_info?.tax_type || 'PERSONAL', validates: [] }),
    name: useField<string>({ value: orderData?.order_invoice_info?.name || orderData?.customer_data?.display_name || 'Khách lẻ', validates: [] }),
    buyer_name: useField<string>({
      value: orderData?.order_invoice_info?.buyer_name || '' || '',
      validates: [],
    }),
    invoice_tax_code: useField<string>({ value: orderData?.order_invoice_info?.tax_code || '', validates: [] }),
    invoice_tax_cccd: useField<string>({ value: orderData?.order_invoice_info?.tax_cccd || '', validates: [] }),
    invoice_email: useField<string>({ value: orderData?.order_invoice_info?.invoice_email || '', validates: [] }),
    invoice_phone: useField<string>({
      value: orderData?.order_invoice_info?.invoice_phone || orderData?.customer_data?.user_phonenumber || '',
      validates: [],
    }),
    invoice_address: useField<string>({ value: orderData?.order_invoice_info?.invoice_address || '', validates: [] }),
    invoice_save_to_invoice_list: useField<string>({ value: '0', validates: [] }),
  };

  const useFields = {
    /** create new one */
    trip_time: useField<string>({ value: '', validates: [] }),
    trip_date: useField<string>({ value: '', validates: [] }),

    /** Edit ... */
    id: useField<string>({ value: `` + orderData?.id, validates: [] }),
    customer_name: useField<string>({ value: currentCustomerData?.display_name ?? '', validates: [] }),
    customer_phone: useField<string>({ value: currentCustomerData?.user_phonenumber ?? '', validates: [] }),
    pickup_time: useField<string>({
      value: orderData?.pickup_time,
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isValidTime(inputVal)) return 'Giờ định dạng sai (hh:mm)';
        },
      ],
    }),

    pickup_address: useField<string>({ value: orderData?.pickup_address || '', validates: [notEmpty('Trường này không được để trống.')] }),

    dropoff_address: useField<string>({
      value: orderData?.dropoff_address || '',
      validates: [notEmpty('Trường này không được để trống.')],
    }),

    dropoff_contact_name: useField<string>({
      value: orderData?.dropoff_contact_name,
      validates: [],
    }),
    dropoff_contact_phonenumber: useField<string>({
      value: orderData?.dropoff_contact_phonenumber,
      validates: [],
    }),

    note: useField<string>({ value: orderData?.note || '', validates: [lengthLessThan(1000, 'Ghi chú quá dài...')] }),

    price_table: useField<{ price: number; discount: number; ticket_id: bigint; ticket_type: string }[]>({
      value: [{ price: 0, discount: 0, ticket_id: BigInt('0'), ticket_type: ENUM_TICKET_TYPE.PASSENGER }],
      validates: [],
    }),

    order_fee: useField<{ fee_value: number; fee_description: string }[]>({
      value: orderData?.order_fee ? orderData.order_fee.map((fD) => ({ fee_value: fD.fee_value, fee_description: fD.fee_description })) : [],
      validates: [],
    }),

    total_fee: useField<number>({
      value: orderData?.total_fee || 0,
      validates: [],
    }),
  };

  // ... sau khi khai báo useForm

  const delayFillData = useCallback(async () => {
    await helpers.sleep(500);
    // 1. Chỉ chạy khi có ticketData và có departure_date
    if (orderData && orderData.departure_date) {
      try {
        // 2. Parse chuỗi ngày giờ từ server
        const departureDate = new Date(Number(orderData.departure_date));

        // 3. Format thành 2 phần riêng biệt
        const timePart = dateandtime.format(departureDate, 'HH:mm');
        const datePart = dateandtime.format(departureDate, 'YYYY-MM-DD');

        // 4. Dùng hàm onChange của field để cập nhật giá trị mới cho form
        useFields.trip_time.onChange(timePart);
        useFields.trip_date.onChange(datePart);
      } catch (error) {
        console.error('Lỗi khi định dạng ngày giờ:', error);
        // Có thể reset field về rỗng nếu ngày giờ lỗi
        useFields.trip_time.onChange('');
        useFields.trip_date.onChange('');
      }
    }
  }, [orderData]);

  useEffect(() => {
    delayFillData();
  }, [orderData]); // Phụ thuộc: Chỉ chạy lại khi ticketData thay đổi

  const { fields, submit, submitting, dirty, makeClean, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        // if (values.payment_status === 1 && !values.casher) {
        //   useFields['casher'].setError(__('check_this_field_again'));
        //   addNotification('error', `Ai là người thu tiền?`);
        //   return { status: 'success', errors: [] };
        // }
        await updateOrder({
          id: values.id,
          customer_name: values.customer_name,
          customer_phone: values.customer_phone,
          pickup_time: values.pickup_time || undefined,
          pickup_address: values.pickup_address,

          note: values.note,
          // price: values.price,
          // fee: values.fee,
          // discount: values.discount,
          price_table: values.price_table,
          order_fee: values.order_fee,

          dropoff_address: values.dropoff_address,
          dropoff_contact_phonenumber: values.dropoff_contact_phonenumber,
          dropoff_contact_name: values.dropoff_contact_name,

          departure_date: new Date(values.trip_date + ' ' + values.trip_time).getTime(), // String(values.trip_time || '').trim() + ' ' + String(values.trip_date || '').trim(),
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  const fromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      fields.customer_phone.onChange(takeNumberOnly(clipboardText));
    } catch (e) {}
  }, []);

  const { mutateAsync: searchUser } = useMutateSearchUser();
  const loadCustomerDataSuggestion = useCallback(async (phonenumber: string) => {
    try {
      let customerData = await searchUser({
        user_phonenumber: phonenumber,
      });
      if (!helpers.isEmpty(customerData)) {
        // @ts-ignore
        setCurrentCustomerData(customerData[0]);
      }
    } catch (e) {}
  }, []);

  const lastSearchedPhone = useRef<string>('');
  const searchCustomerDebounce = useMemo(
    () =>
      helpers.debounce((value: string) => {
        if (!value || value === lastSearchedPhone.current) return;
        lastSearchedPhone.current = value;
        loadCustomerDataSuggestion(value);
      }, 400),
    [loadCustomerDataSuggestion]
  );

  useEffect(() => {
    const normalizedPhone = takeNumberOnly(fields.customer_phone.value);
    if (helpers.isPhoneNumber(normalizedPhone)) {
      searchCustomerDebounce(normalizedPhone);
    } else {
      lastSearchedPhone.current = '';
    }
  }, [fields.customer_phone.value, searchCustomerDebounce]);

  /** reset lai form */
  useEffect(() => {
    helpers.resetForm(useFields);
    helpers.resetForm(invoiceFields);
    makeClean();
  }, [show]);

  /** OLD */
  useEffect(() => {
    if (isSuccess) {
      addNotification('info', 'Hủy vé thành công!');
      onClose(true);
    }
  }, [isSuccess]);
  useEffect(() => {
    if (updateSuccess) {
      addNotification('info', 'Cập nhật vé thành công!');
      onClose(true);
    }
  }, [updateSuccess]);

  const orderCancelCallback = useCallback(async () => {
    let result = window.confirm('Bạn có thực sự muốn hủy đơn này và các vé liên quan?');
    try {
      if (result) await orderCancel(order_id);
    } catch (e) {}
  }, [order_id]);

  /** tính toán tổng phải thanh toán */
  const [totalMustPay, setTotalMustPay] = useState<number>(0);

  useEffect(() => {
    const fee = Number(fields.total_fee.value || 0);
    let totalMustPay = 0;

    let allPriceTable = useFields.price_table.value;
    for (let priceTag of allPriceTable) {
      totalMustPay += priceTag.price - priceTag.discount;
    }
    setTotalMustPay(totalMustPay + fee);
  }, [fields.price_table.value, fields.total_fee.value]);

  useEffect(() => {
    let total_fee = 0;
    for (let d of useFields.order_fee.value || []) {
      total_fee += Number(d.fee_value || 0);
    }
    useFields.total_fee.onChange(total_fee);
  }, [useFields.order_fee.value]);

  const handleAddOrderFee = useCallback(() => {
    useFields.order_fee.onChange([...useFields.order_fee.value, { fee_description: 'PICKUP_FEE', fee_value: 0 }]);
  }, [useFields.order_fee.value]);

  const handleRemoveOrderFee = useCallback(
    (index: number) => {
      const next = useFields.order_fee.value.filter((_, i) => i !== index);
      useFields.order_fee.onChange(next);
    },
    [useFields.order_fee.value]
  );

  /** set gía khi edit */
  const [has_shipping_package, setHas_shipping_package] = useState(false);
  useEffect(() => {
    if (!orderData) return;
    let price_table = [];
    for (let ticket of orderData.ticket) {
      // chỉ cần một vé có giao hàng ...
      if (ticket.ticket_type === ENUM_TICKET_TYPE.SHIPMENT) setHas_shipping_package(true);
      price_table.push({ price: ticket?.price, discount: ticket?.discount, ticket_id: ticket.ticket_id });
    }
    useFields.price_table.onChange(price_table);
  }, [orderData]);

  // Nuts copy
  const copyReceiverFromSenderCallback = useCallback(() => {
    useFields.dropoff_contact_name.onChange(useFields.customer_name.value);
    useFields.dropoff_contact_phonenumber.onChange(useFields.customer_phone.value);
  }, [useFields.customer_name.value, useFields.customer_phone.value]);

  return (
    <>
      <Modal
        size="large"
        open={show}
        title={`Chi tiết đơn hàng #${orderData?.order_code} - ${currentCustomerData ? `${currentCustomerData?.display_name} - ${currentCustomerData?.user_phonenumber}` : 'Chưa có thông tin khách hàng'}`}
        onClose={() => onClose(false)}
        // secondaryActions={[
        //   {
        //     content: 'Hủy đơn & toàn bộ vé',
        //     icon: XSmallIcon,
        //     disabled: cancelling,
        //     destructive: true,

        //     onAction: () => orderCancelCallback(),
        //   },
        // ]}
        primaryAction={{
          content: `Cập nhật`,
          icon: ConfettiIcon,
          disabled: cancelling || !dirty || totalMustPay < 1,
          loading: submitting,
          onAction: submit,
        }}
      >
        <Modal.Section>
          <Form onSubmit={submit}>
            <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Grid.Cell>
                <BlockStack gap={{ xs: '200', sm: '400' }}>
                  <FormLayout.Group>
                    <TextField
                      label="Số điện thoại"
                      autoComplete="off"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      disabled={submitting}
                      clearButton
                      value={fields.customer_phone.value}
                      labelAction={{
                        content: 'Dán',
                        onAction: () => {
                          fromClipboard();
                        },
                      }}
                      onChange={(v) => {
                        fields.customer_phone.onChange(takeNumberOnly(String(v)));
                      }}
                      error={fields.customer_phone.error}
                      onClearButtonClick={() => {
                        fields.customer_phone.onChange('');
                        setCurrentCustomerData(null);
                      }}
                    />

                    <TextField
                      autoComplete="off"
                      placeholder="Tên khách hàng"
                      label="Khách hàng"
                      {...fields.customer_name}
                      onChange={(e) => fields.customer_name.onChange(helpers.capitalizeWords(e))}
                      disabled={submitting}
                    />
                  </FormLayout.Group>

                  <Divider />

                  <TextField
                    autoComplete="off"
                    placeholder="Địa chỉ đón"
                    label={`Đón tại ` + orderData?.journey?.journey_from}
                    disabled={submitting}
                    {...useFields.pickup_address}
                  />

                  <TextField
                    autoComplete="off"
                    placeholder="Địa chỉ xuống"
                    label={`Xuống tại ` + orderData?.journey?.journey_to}
                    disabled={submitting}
                    {...useFields.dropoff_address}
                  />
                </BlockStack>

                {has_shipping_package ? (
                  <BlockStack gap="400" inlineAlign="start">
                    <Divider />
                    <Text as="h3" variant="headingMd">
                      Thông tin người nhận hàng
                    </Text>
                    <FormLayout.Group condensed>
                      <TextField
                        autoComplete="off"
                        label={`Tên người nhận hàng`}
                        {...fields.dropoff_contact_name}
                        requiredIndicator
                        disabled={submitting}
                      />
                      <TextField
                        autoComplete="off"
                        label={`Số liên lạc`}
                        {...fields.dropoff_contact_phonenumber}
                        requiredIndicator
                        disabled={submitting}
                      />
                    </FormLayout.Group>
                    <Button
                      icon={DuplicateIcon}
                      variant="plain"
                      onClick={() => {
                        copyReceiverFromSenderCallback();
                      }}
                    >
                      Copy thông tin từ người đặt
                    </Button>
                  </BlockStack>
                ) : null}
              </Grid.Cell>
              <Grid.Cell>
                <BlockStack gap={{ xs: '200', sm: '400' }}>
                  <Text as="h3" variant="headingMd">
                    {orderData?.journey?.journey_name}
                  </Text>
                  <FormLayout.Group condensed>
                    <TextField autoComplete="off" label={`Thời gian`} placeholder="hh:mm" {...fields.trip_time} disabled={submitting} />
                    <TextField autoComplete="off" label={`Đón lúc`} placeholder="hh:mm" {...fields.pickup_time} disabled={submitting} />
                    <TextField autoComplete="off" label={`Ngày đi`} placeholder="2025-06-08" {...fields.trip_date} disabled={submitting} />
                  </FormLayout.Group>
                  <Divider />

                  <TextField
                    autoComplete="off"
                    multiline={2}
                    label="Ghi chú"
                    showCharacterCount
                    maxLength={1000}
                    {...fields.note}
                    disabled={submitting}
                  />

                  <Text as="h3" variant="headingMd">
                    Các chi phí
                  </Text>
                  {useFields.order_fee.value?.map((feeData, index) => {
                    return (
                      <FormLayout.Group key={`order_fee_${index}`}>
                        <Select
                          label="Tên"
                          options={helpers.enumToArray(ENUM_ORDER_FEE).map((el) => {
                            return {
                              label: ORDER_FEE_LABEL[el],
                              value: el,
                            };
                          })}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.order_fee.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], fee_description: a };
                            useFields.order_fee.onChange(updatedPriceTable);
                          }}
                          value={feeData.fee_description}
                        />
                        <TextField
                          autoComplete="off"
                          label={`Số tiền`}
                          type="tel"
                          placeholder="Số tiền"
                          inputMode="numeric"
                          pattern="[0-9.,]*"
                          value={__helpers.formatNumber(feeData.fee_value)}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.order_fee.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], fee_value: __helpers.parseNumeric(a) };
                            useFields.order_fee.onChange(updatedPriceTable);
                          }}
                          disabled={submitting}
                          labelAction={{
                            content: 'Xoá',
                            onAction: () => handleRemoveOrderFee(index),
                          }}
                        />
                      </FormLayout.Group>
                    );
                  })}
                  <div>
                    <Button icon={PlusIcon} variant="plain" onClick={handleAddOrderFee}>
                      Thêm phí
                    </Button>
                  </div>

                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" fontWeight="bold">
                      Tổng phí
                    </Text>
                    <Text as="h3" fontWeight="bold" variant="headingMd" tone="critical">
                      {__helpers.formatNumber(fields.total_fee.value)} VND
                    </Text>
                  </InlineStack>

                  <Divider />

                  <Text as="h3" variant="headingMd">
                    Vé và giá vé
                  </Text>

                  {useFields.price_table.value.map((el, index) => {
                    return (
                      <FormLayout.Group condensed key={`price_${index}_${el.ticket_id}`}>
                        <TextField
                          autoComplete="off"
                          label={`Giá vé #${index + 1}`}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9.,]*"
                          value={__helpers.formatNumber(el.price)}
                          // onChange={(a) => {
                          //   fields.price.onChange(helpers.parseNumeric(a));
                          // }}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.price_table.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], price: __helpers.parseNumeric(a) };
                            useFields.price_table.onChange(updatedPriceTable);
                          }}
                          disabled={submitting}
                        />
                        <TextField
                          autoComplete="off"
                          label="Giảm giá"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={__helpers.formatNumber(el.discount)}
                          // onChange={(a) => {
                          //   fields.discount.onChange(helpers.parseNumeric(a));
                          // }}
                          onChange={(a) => {
                            const updatedPriceTable = [...useFields.price_table.value];
                            updatedPriceTable[index] = { ...updatedPriceTable[index], discount: __helpers.parseNumeric(a) };
                            useFields.price_table.onChange(updatedPriceTable);
                          }}
                          disabled={submitting}
                        />
                      </FormLayout.Group>
                    );
                  })}

                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" fontWeight="bold">
                      Tổng phải thanh toán
                    </Text>
                    <Text as="h3" fontWeight="bold" variant="headingLg" tone="critical">
                      {__helpers.formatNumber(totalMustPay)} VND
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Grid.Cell>
            </Grid>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}

